import { useEffect, useRef, useState, type RefObject } from "react";
import { debounce } from "lodash";
import { logger } from "@/lib/logger";

/** Long enough to coalesce a window drag, short enough that the grid keeps up with it. */
export const DASHBOARD_GRID_WIDTH_DEBOUNCE_MS = 50;

/**
 * More width changes than the debounce lets a human produce: dragging a window
 * edge yields at most 20 a second, and it stops when the drag does.
 */
export const DASHBOARD_GRID_LOOP_CHANGE_THRESHOLD = 100;

/** Every real interaction leaves a gap this long. A layout that oscillates never does. */
export const DASHBOARD_GRID_LOOP_QUIET_MS = 2000;

/**
 * Floor, never round.
 *
 * `offsetWidth` is the border-box width rounded to an integer, so a container
 * laid out at 1279.6px reports 1280px (measured in Chrome: 1279.59375 →
 * offsetWidth 1280, 0.41px wider than the box it sits in). react-grid-layout
 * positions its right-hand column against exactly that number, so the grid
 * overflowed its own scrollport, and an overflow toggles a scrollbar, which
 * resizes the container, which remeasures.
 *
 * LAR-435: that is the loop the browser reports as "ResizeObserver loop
 * completed with undelivered notifications", ~30 times a second for as long as
 * the tab stays open. Fractional widths only happen at some viewport widths and
 * zoom levels, which fits three users hitting it for hours rather than everyone
 * hitting it briefly — though nobody has reproduced their exact layout, so the
 * detector below reports the loop from production if it survives this.
 */
function measureWidth(element: HTMLElement): number {
  return Math.floor(element.getBoundingClientRect().width);
}

/**
 * Matches DashboardGrid: tracks the scroll/content width for react-grid-layout.
 */
export function useDashboardGridContainerWidth(): {
  containerRef: RefObject<HTMLDivElement | null>;
  containerWidth: number;
} {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let published = 0;
    let changedAt = 0;
    let changesWithoutAPause = 0;
    let loopReported = false;

    const reportIfLooping = (width: number) => {
      const now = Date.now();
      changesWithoutAPause =
        now - changedAt > DASHBOARD_GRID_LOOP_QUIET_MS
          ? 1
          : changesWithoutAPause + 1;
      changedAt = now;

      if (loopReported) return;
      if (changesWithoutAPause < DASHBOARD_GRID_LOOP_CHANGE_THRESHOLD) return;

      loopReported = true;
      logger.warn("Dashboard grid container width is not settling", {
        operation: "dashboard.grid.measureContainerWidth",
        containerWidth: width,
        changesWithoutAPause,
      });
    };

    // Republishing the same width would re-render the grid for nothing, and a
    // container that has settled must go quiet however often the observer fires.
    const measure = () => {
      const current = containerRef.current;
      if (!current) return;

      const width = measureWidth(current);
      if (width === published) return;

      published = width;
      setContainerWidth(width);
      reportIfLooping(width);
    };

    const updateWidth = debounce(measure, DASHBOARD_GRID_WIDTH_DEBOUNCE_MS);

    measure();

    // Remeasure after paint when the width was 0 (e.g. display:none). ResizeObserver
    // usually catches show/hide; callers may still need to handle persistent display:none.
    let rafOuter = 0;
    let rafInner = 0;
    rafOuter = requestAnimationFrame(() => {
      rafInner = requestAnimationFrame(measure);
    });

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(el);

    return () => {
      cancelAnimationFrame(rafOuter);
      cancelAnimationFrame(rafInner);
      resizeObserver.disconnect();
      updateWidth.cancel();
    };
  }, []);

  return { containerRef, containerWidth };
}
